import LoginForm from 'components/Login/LoginForm';
import Preloader from 'components/Preloader';
import { Login, LoginOAuth } from 'models/request';
import { UserData } from 'models/user';
import qs from 'query-string';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import service from 'services';
import actions from 'state/actions';
import selectors from 'state/selectors';
import styled from 'styled-components/macro';
import { LoginBlocked } from 'utils/enums';
import appHelpers from 'utils/helpers/app.helpers';
import { modalActionType } from 'utils/constants';

import action from 'state/actions';

import ModalsContent from './components/ModalsContent';

import frontEndInfo from '../../../package.json';

const Center = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: inherit;
`;

export default () => {
  const [errorMessage, setErrorMessage] = React.useState<string>('');
  const { logo, openIdUrl } = useSelector(selectors.app.getAppStyle) || {};
  const history = useHistory();
  const dispatch = useDispatch();
  const beBuildNumber = useSelector(selectors.app.getBuildNumber);

  const checkLogin = React.useCallback(
    (status: number, user: UserData) => {
      if (status === 401) setErrorMessage('wrong_credentials');
      if (status === 200) {
        const { token, blocked } = user;
        if (token && blocked === 0) {
          localStorage.setItem('jwt', token);
          dispatch(actions.user.performUserLogin(user));
          dispatch(actions.chatbot.onUserLogin());

          history.push('/');
        } else {
          dispatch(actions.user.setAuthData(user));
          switch (blocked) {
            case LoginBlocked.PASSWORD_ERROR: {
              setErrorMessage('password_error');
              break;
            }
            case LoginBlocked.GENERAL_BLOCKED: {
              setErrorMessage('user_blocked');
              break;
            }
            case LoginBlocked.JDBC_BLOCKED: {
              setErrorMessage('login_jdbc_blocked');
              break;
            }
            case LoginBlocked.DUE_DATE_BLOCKED: {
              setErrorMessage('user_expired');
              break;
            }
            case LoginBlocked.MAX_LOGIN_BLOCKED: {
              setErrorMessage('user_blocked_max_attempts');
              break;
            }
            case LoginBlocked.IP_BLOCKED: {
              setErrorMessage('ip_blocked');
              break;
            }
            case LoginBlocked.CHANGE_PASSWORD: {
              dispatch(action.modal.openModal(modalActionType.USER_PASSWORD));
              break;
            }
            case LoginBlocked.TEMPORARY_PASSWORD_EXPIRED: {
              setErrorMessage('temporary_password_expired');
              break;
            }
            default: {
              setErrorMessage('login_general_error');
            }
          }
        }
      }
    },
    [dispatch, history],
  );

  const performOauthLogin = React.useCallback(
    async (loginData: LoginOAuth) => {
      try {
        const { status, data: user } = await service.oAuthLogin(loginData.code, loginData.accessToken);
        checkLogin(status, user);
      } catch (err) {
        setErrorMessage('login_general_error');
        appHelpers.notify('fail', err as Error, 5000);
      }
    },
    [checkLogin],
  );

  const performLogin = React.useCallback(
    async (loginData: Login) => {
      try {
        const { username, password } = loginData;
        if (username === null || password === null) throw new Error('Username and Password should not be null');
        const { status, data: user } = await service.login(username, password);
        checkLogin(status, user);
      } catch (err) {
        setErrorMessage('login_general_error');
        appHelpers.notify('fail', err as Error, 5000);
      }
    },
    [checkLogin],
  );

  React.useEffect(() => {
    try {
      if (window.location.search) {
        // OAUTH LOGIN PARAMS FROM URL
        const { code, access_token: accessToken = '' } = qs.parse(window.location.search);
        if (!(code && typeof code === 'string' && accessToken !== null && typeof accessToken === 'string')) {
          throw new Error('Oauth login: Something went wrong with login params');
        }
        performOauthLogin({ code, accessToken });
      }
    } catch (err) {
      appHelpers.notify('fail', err as Error, 5000);
    }
  }, [performOauthLogin]);

  const openModalResetPassword = () => {
    dispatch(actions.modal.openModal(modalActionType.RESET_PASSWORD));
  };

  return (
    <Center>
      <ModalsContent />
      <Preloader area="app" />
      <LoginForm
        message={errorMessage}
        oauthLoginLink={openIdUrl}
        logo={logo}
        performLogin={performLogin}
        setErrorMessage={setErrorMessage}
        version={`Version - FE: ${frontEndInfo.version} / BE: ${beBuildNumber}`}
        resetPassword={openModalResetPassword}
      />
    </Center>
  );
};
